/**
 * Hook para acesso a indicações, pins e estatísticas.
 *
 * Camada obrigatória entre os componentes e o store — nenhum componente
 * deve acessar o store diretamente ou fazer fetch por conta própria.
 *
 * @module hooks/useIndicacoes
 */

import { useCallback, useEffect } from "react";
import useStore from "../store/indicacoesSlice";

/**
 * Fornece os pins geocodificados para o mapa.
 *
 * Carrega automaticamente na montagem do componente.
 *
 * @returns {{
 *   pins: import("../store/indicacoesSlice").GeoPin[],
 *   loading: boolean,
 *   error: string|null,
 *   reload: () => Promise<void>
 * }}
 */
export function usePins() {
  const pins = useStore((s) => s.pins);
  const cityPins = useStore((s) => s.cityPins);
  const loading = useStore((s) => s.pinsLoading);
  const error = useStore((s) => s.pinsError);
  const loadPins = useStore((s) => s.loadPins);

  useEffect(() => {
    loadPins();
  }, [loadPins]);

  return { pins, cityPins, loading, error, reload: loadPins };
}

/**
 * Fornece a listagem paginada de indicações.
 *
 * Carrega automaticamente na montagem do componente.
 *
 * @returns {{
 *   indicacoes: import("../store/indicacoesSlice").Indicacao[],
 *   total: number,
 *   page: number,
 *   pages: number,
 *   pageSize: number,
 *   loading: boolean,
 *   error: string|null,
 *   goToPage: (page: number) => Promise<void>,
 *   reload: () => Promise<void>
 * }}
 */
export function useIndicacoes() {
  const indicacoes = useStore((s) => s.indicacoes);
  const total = useStore((s) => s.indicacoesTotal);
  const page = useStore((s) => s.indicacoesPage);
  const pages = useStore((s) => s.indicacoesPages);
  const pageSize = useStore((s) => s.indicacoesPageSize);
  const loading = useStore((s) => s.indicacoesLoading);
  const error = useStore((s) => s.indicacoesError);
  const loadIndicacoes = useStore((s) => s.loadIndicacoes);

  useEffect(() => {
    loadIndicacoes();
  }, [loadIndicacoes]);

  const goToPage = useCallback(
    /** @param {number} p */
    (p) => loadIndicacoes(p),
    [loadIndicacoes]
  );

  return {
    indicacoes,
    total,
    page,
    pages,
    pageSize,
    loading,
    error,
    goToPage,
    reload: loadIndicacoes,
  };
}

/**
 * Fornece o detalhe de uma indicação selecionada e controles de geometria.
 *
 * @returns {{
 *   selected: import("../store/indicacoesSlice").Indicacao|null,
 *   loading: boolean,
 *   error: string|null,
 *   isGeometryVisible: boolean,
 *   focusMapTrigger: number,
 *   select: (id: number) => Promise<void>,
 *   clear: () => void,
 *   toggleGeometryVisible: (forcedValue?: boolean) => void,
 *   triggerFocusMap: () => void
 * }}
 */
export function useSelectedIndicacao() {
  const selected = useStore((s) => s.selectedIndicacao);
  const loading = useStore((s) => s.selectedLoading);
  const error = useStore((s) => s.selectedError);
  const isGeometryVisible = useStore((s) => s.isGeometryVisible);
  const focusMapTrigger = useStore((s) => s.focusMapTrigger);
  const select = useStore((s) => s.selectIndicacao);
  const clear = useStore((s) => s.clearSelection);
  const toggleGeometryVisible = useStore((s) => s.toggleGeometryVisible);
  const triggerFocusMap = useStore((s) => s.triggerFocusMap);

  return {
    selected,
    loading,
    error,
    isGeometryVisible,
    focusMapTrigger,
    select,
    clear,
    toggleGeometryVisible,
    triggerFocusMap,
  };
}

/**
 * Fornece estatísticas gerais e ação de sincronização.
 *
 * Carrega automaticamente na montagem do componente.
 *
 * @returns {{
 *   stats: import("../store/indicacoesSlice").Stats|null,
 *   loading: boolean,
 *   error: string|null,
 *   reload: () => Promise<void>,
 *   syncing: boolean,
 *   syncMessage: string|null,
 *   triggerSync: () => Promise<void>
 * }}
 */
export function useStats() {
  const stats = useStore((s) => s.stats);
  const loading = useStore((s) => s.statsLoading);
  const error = useStore((s) => s.statsError);
  const loadStats = useStore((s) => s.loadStats);
  const syncing = useStore((s) => s.syncing);
  const syncMessage = useStore((s) => s.syncMessage);
  const triggerSyncAction = useStore((s) => s.triggerSync);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    stats,
    loading,
    error,
    reload: loadStats,
    syncing,
    syncMessage,
    triggerSync: triggerSyncAction,
  };
}

/**
 * Fornece dados e ações de revisão manual.
 *
 * @returns {{
 *  reviewAddresses: any[],
 *  reviewIndicacoes: any[],
 *  loading: boolean,
 *  error: string|null,
 *  loadReview: (status?: string) => Promise<void>,
 *  createReviewAddress: (data: {indicacao_id: number, street: string, number?: string|null, bairro?: string|null}) => Promise<void>,
 *  updateReviewAddress: (enderecoId: number, payload: {street?: string, number?: string|null, bairro?: string|null}) => Promise<void>,
 *  deleteReviewAddress: (enderecoId: number) => Promise<void>,
 *  clearAdminState: () => void
 * }}
 */
export function useReview() {
  const reviewAddresses = useStore((s) => s.reviewAddresses);
  const reviewIndicacoes = useStore((s) => s.reviewIndicacoes);
  const loading = useStore((s) => s.reviewLoading);
  const error = useStore((s) => s.reviewError);
  const loadReview = useStore((s) => s.loadReview);
  const createReviewAddress = useStore((s) => s.createReviewAddress);
  const updateReviewAddress = useStore((s) => s.updateReviewAddress);
  const deleteReviewAddress = useStore((s) => s.deleteReviewAddress);
  const clearAdminState = useStore((s) => s.clearAdminState);

  return {
    reviewAddresses,
    reviewIndicacoes,
    loading,
    error,
    loadReview,
    createReviewAddress,
    updateReviewAddress,
    deleteReviewAddress,
    clearAdminState,
  };
}
